import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';

/**
 * Offers to put MoreMe AI on the home screen / desktop as an installed app.
 *
 * Chrome, Edge, Brave and Samsung Internet (desktop and Android) fire
 * `beforeinstallprompt`; we hold that event and call `prompt()` when the
 * person taps Install. iOS Safari never fires it, so there the sheet walks
 * through Share → Add to Home Screen instead.
 *
 * Presentation:
 *   - phones and tablets on the sign-in page: a bottom sheet (the "popup")
 *   - everywhere else: the compact corner snackbar
 *
 * A dismissal is remembered per browser for 14 days. `public/index.html`
 * captures `beforeinstallprompt` before React mounts and parks it on
 * `window.__mmInstallPrompt`, so an event that fires early is not lost.
 */
type DeferredPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

declare global {
  interface Window {
    __mmInstallPrompt?: DeferredPrompt | null;
  }
}

const DISMISS_KEY = 'pwa.install.dismissedAt';
const COOKIE_CONSENT_KEY = 'cookie_consent.v1'; // see CookieConsentBanner

// Offer the install only once the cookie banner has been answered: on a first
// visit to the sign-in page both opened at the bottom and the banner covered
// the sheet's buttons.
function cookieChoiceMade(): boolean {
  try { return localStorage.getItem(COOKIE_CONSENT_KEY) !== null; } catch { return true; }
}
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // re-offer after 14 days
const SETTLE_MS = 2000; // let the page land before offering

function recentlyDismissed(): boolean {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (!ts || Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

function isIOSSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  if (!isIOS) return false;
  // Chrome / Firefox / Edge / Opera on iOS cannot add to the home screen.
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(
    (window.navigator as any).standalone ||
    window.matchMedia?.('(display-mode: standalone)').matches
  );
}

export default function InstallPrompt() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const onLogin = pathname === '/login';

  const [deferred, setDeferred] = useState<DeferredPrompt | null>(
    () => (typeof window !== 'undefined' ? window.__mmInstallPrompt ?? null : null)
  );
  const [dismissed, setDismissed] = useState<boolean>(recentlyDismissed);
  const [settled, setSettled] = useState(false);
  const [ios] = useState(() => isIOSSafari() && !isStandalone());
  const [installed] = useState(isStandalone);
  const [consentAnswered, setConsentAnswered] = useState(cookieChoiceMade);

  useEffect(() => {
    const onConsent = () => setConsentAnswered(true);
    window.addEventListener('cookie-consent-changed', onConsent);
    return () => window.removeEventListener('cookie-consent-changed', onConsent);
  }, []);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      window.__mmInstallPrompt = e as DeferredPrompt;
      setDeferred(e as DeferredPrompt);
    };
    const onInstalled = () => {
      window.__mmInstallPrompt = null;
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    const t = window.setTimeout(() => setSettled(true), SETTLE_MS);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.clearTimeout(t);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'dismissed') {
        markDismissed();
        setDismissed(true);
      }
    } finally {
      // A prompt event can only be used once.
      window.__mmInstallPrompt = null;
      setDeferred(null);
    }
  };

  const handleDismiss = () => {
    markDismissed();
    setDismissed(true);
  };

  if (installed || dismissed || !settled || !consentAnswered) return null;

  const canInstall = Boolean(deferred);
  if (!canInstall && !ios) return null;

  if (isMobile && onLogin) {
    return (
      <InstallSheet
        ios={!canInstall}
        onInstall={handleInstall}
        onDismiss={handleDismiss}
      />
    );
  }

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: canInstall ? 'right' : 'center' }}
      sx={{ mb: { xs: 0, md: 2 } }} // the theme lifts bottom snackbars above the mobile bottom nav
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: 'center', '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1 } }}
        action={
          <>
            {canInstall && (
              <Button color="inherit" size="small" onClick={handleInstall}>Install</Button>
            )}
            <IconButton color="inherit" size="small" onClick={handleDismiss} aria-label="Dismiss install prompt">
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        {canInstall ? (
          'Install MoreMe AI for offline access and a native-app feel.'
        ) : (
          <>
            <Typography variant="body2">Install MoreMe AI: tap</Typography>
            <IosShareIcon fontSize="small" />
            <Typography variant="body2">then "Add to Home Screen".</Typography>
          </>
        )}
      </Alert>
    </Snackbar>
  );
}

/**
 * The sign-in popup on phones and tablets. Slides up from the bottom so it
 * reads as part of the page rather than a system alert, and stays clear of
 * the home indicator on notched devices.
 */
function InstallSheet({
  ios,
  onInstall,
  onDismiss,
}: {
  ios: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}) {
  return (
    <Drawer
      anchor="bottom"
      open
      onClose={onDismiss}
      PaperProps={{
        sx: {
          borderRadius: '24px 24px 0 0',
          px: 3,
          pt: 1.5,
          pb: 'calc(24px + env(safe-area-inset-bottom))',
          maxWidth: 560,
          mx: 'auto',
        },
      }}
      aria-labelledby="install-sheet-title"
    >
      <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mb: 2.5 }} />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
        <Box
          component="img"
          src="/android-chrome-192x192.png"
          alt=""
          sx={{ width: 56, height: 56, borderRadius: '14px', flexShrink: 0, boxShadow: 1 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="install-sheet-title" sx={{ fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.25 }}>
            Add MoreMe AI to your home screen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Opens full-screen like an app, with nothing to download from a store.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onDismiss} aria-label="Not now" sx={{ alignSelf: 'flex-start', mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {ios ? (
        <>
          <Stack spacing={1.25} sx={{ mb: 3 }}>
            <Step n={1}>
              Tap <IosShareIcon sx={{ fontSize: 20, verticalAlign: 'text-bottom', mx: 0.5 }} /> <b>Share</b> in Safari's toolbar
            </Step>
            <Step n={2}>
              Choose <AddBoxOutlinedIcon sx={{ fontSize: 20, verticalAlign: 'text-bottom', mx: 0.5 }} /> <b>Add to Home Screen</b>
            </Step>
            <Step n={3}>
              Tap <b>Add</b> in the top corner
            </Step>
          </Stack>
          <Button fullWidth variant="contained" size="large" onClick={onDismiss} sx={{ borderRadius: '16px' }}>
            Got it
          </Button>
        </>
      ) : (
        <Stack spacing={1}>
          <Button fullWidth variant="contained" size="large" onClick={onInstall} sx={{ borderRadius: '16px' }}>
            Install app
          </Button>
          <Button fullWidth variant="text" size="large" onClick={onDismiss} sx={{ borderRadius: '16px' }}>
            Not now
          </Button>
        </Stack>
      )}
    </Drawer>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          display: 'grid', placeItems: 'center',
          bgcolor: 'primary.main', color: 'primary.contrastText',
          fontWeight: 700, fontSize: '0.85rem',
        }}
      >
        {n}
      </Box>
      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{children}</Typography>
    </Stack>
  );
}
